import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import apiClient from '../utils/apiClient';
import toast from 'react-hot-toast';
import { useGlobalData } from '../hooks/useGlobalHooks';
import server from '../utils/serverConfig';
import WebSocketManager from '../contexts/websocketManager';
import { useWebSocketStatusStore } from '../contexts/LTPStore/useWebSocketStatusStore';
import { EditFyersUserModal } from '../Dashboard/BotConfigs/FyersUsersList/EditFyersUserModal';
import { EditNubraUserModal } from '../Dashboard/BotConfigs/FyersUsersList/EditNubraUserModal';
import { EditDhanXTSUserModal } from '../Dashboard/BotConfigs/FyersUsersList/EditDhanXTSUserModal';
import { EditDhanHQUserModal } from '../Dashboard/BotConfigs/FyersUsersList/EditDhanHQUserModal';
import { EditIndexOptionsModal } from '../Dashboard/BotConfigs/IndexOptionsMultiplier/EditIndexOptionsModal';
import { AddNewUserModal } from '../Dashboard/BotConfigs/FyersUsersList/addNewUserModal';
import { Pencil } from 'lucide-react';
/**
 * Get broker badge info based on broker name
 * @param {string} broker - Broker name
 * @returns {Object} Badge info with text and color classes
 */
function getBrokerBadge(broker) {
  const brokerLower = broker?.toLowerCase() || '';

  if (brokerLower === 'fyers') {
    return { text: 'FY', class: 'bg-blue-800 text-white' };
  } else if (brokerLower === 'dhan_ss') {
    return { text: 'DS', class: 'bg-emerald-600 text-white' };
  } else if (brokerLower === 'dhan_xts') {
    return { text: 'DX', class: 'bg-teal-600 text-white' };
  } else if (brokerLower === 'dhan_hq') {
    return { text: 'DHQ', class: 'bg-emerald-950 text-white' };
  } else if (brokerLower.includes('dhan')) {
    return { text: 'DH', class: 'bg-emerald-800 text-white' };
  } else if (brokerLower === 'nubra') {
    return { text: 'NU', class: 'bg-purple-800 text-white' };
  } else if (brokerLower === 'zerodha') {
    return { text: 'ZE', class: 'bg-red-800 text-white' };
  }

  return { text: '??', class: 'bg-gray-700 text-white' };
}

/**
 * Copy text to clipboard
 */
function copyToClipboard(text, label) {
  if (!text) return;
  navigator.clipboard.writeText(String(text)).then(() => {
    toast.success(`${label} copied to clipboard`);
  }).catch(err => {
    console.error('Failed to copy:', err);
  });
}

/**
 * Get broker chip color classes
 */
function getBrokerChipColor(broker) {
  const brokerLower = broker?.toLowerCase() || '';

  if (brokerLower === 'fyers') {
    return 'bg-blue-800 text-white border border-blue-900';
  } else if (brokerLower === 'dhan_ss') {
    return 'bg-emerald-600 text-white border border-emerald-700';
  } else if (brokerLower === 'dhan_xts') {
    return 'bg-teal-600 text-white border border-teal-700';
  } else if (brokerLower === 'dhan_hq') {
    return 'bg-emerald-950 text-white border border-emerald-950';
  } else if (brokerLower.includes('dhan')) {
    return 'bg-emerald-800 text-white border border-emerald-900';
  } else if (brokerLower === 'nubra') {
    return 'bg-purple-800 text-white border border-purple-900';
  }
  else if (brokerLower === 'zerodha') {
    return 'bg-red-800 text-white border border-purple-900';
  }

  return 'bg-gray-500 text-white border border-gray-800';
}

/**
 * Format broker name with first letter capitalized
 */
function formatBrokerName(broker) {
  if (!broker) return '-';

  const brokerLower = broker.toLowerCase();

  // Handle special cases
  if (brokerLower === 'dhan_ss') return 'Dhan SS';
  if (brokerLower === 'dhan_xts') return 'Dhan XTS';
  if (brokerLower === 'dhan_anurag') return 'Dhan Anurag';
  if (brokerLower === 'dhan_hq') return 'DHQ';
  if (brokerLower.includes('dhan')) {
    // Capitalize each word for other dhan variants
    return broker.split('_').map(word =>
      word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    ).join(' ');
  }

  // For Fyers, Nubra, etc - just capitalize first letter
  return broker.charAt(0).toUpperCase() + broker.slice(1).toLowerCase();
}

// Small helper to choose badge color classes based on broker token
function getBrokerClass(brokerLower) {
  if (!brokerLower) return 'bg-gray-700 text-white border border-gray-800';
  if (brokerLower.includes('fyers')) return 'bg-blue-800 text-white border border-blue-900';
  if (brokerLower === 'dhan_ss') return 'bg-emerald-600 text-white border border-emerald-700';
  if (brokerLower === 'dhan_xts') return 'bg-teal-600 text-white border border-teal-700';
  if (brokerLower === 'dhan_hq') return 'bg-emerald-950 text-white border border-emerald-950';
  if (brokerLower.includes('dhan')) return 'bg-emerald-800 text-white border border-emerald-900';
  if (brokerLower.includes('nubra')) return 'bg-purple-800 text-white border border-purple-900';
  if (brokerLower.includes('zerodha')) return 'bg-[#EF5350] text-white border border-[#EE4419]';
  return 'bg-gray-700 text-white border border-gray-800';
}

// Render broker as one or more small badges (handles values like 'dhan_anurag' or 'fyers')
const renderBrokerBadges = (broker) => {
  // Render broker as a single badge and prevent wrapping so long names stay on one line
  if (!broker) return <span className="text-zinc-400">-</span>;

  const display = formatBrokerName(broker);
  const cls = getBrokerClass(String(broker).toLowerCase());

  return (
    <div className="flex items-center justify-center">
      <span className={`px-2 py-0.5 rounded text-[10px] font-semibold ${cls} whitespace-nowrap`}
        title={display}
      >
        {display}
      </span>
    </div>
  );
};

/**
 * Format number with commas
 */
function formatNumber(num) {
  if (num === undefined || num === null) return '-';
  return num.toLocaleString('en-IN');
}

/**
 * Helper to compute multiplier cell class (green highlight when value > 1)
 */
function mulCellClass(val) {
  const n = Number(val ?? 0);
  // highlight when value > 0 (exclude zero/undefined)
  if (n > 0) return 'py-2 px-4 text-center bg-emerald-600 text-white font-semibold rounded';
  return 'py-2 px-4 text-center text-gray-300';
}

/**
 * AccountView Component
 * Displays account-level information for all clients with tabs
 * 
 * @param {Object} props
 * @param {Array} props.data - Account view data from useAccountView hook (already has all the data!)
 * @param {boolean} props.loading - Loading state
 */
export function AccountView({ data, loading }) {
  const [activeTab, setActiveTab] = useState('details');
  const [userDetails, setUserDetails] = useState({});
  const [loadingStates, setLoadingStates] = useState({});
  // Full Edit Modal State
  const [fullEditModal, setFullEditModal] = useState({ isOpen: false, type: null, user: null });

  // Loading state for refetching
  const [isRefetching, setIsRefetching] = useState(false);
  const [editModal, setEditModal] = useState({ isOpen: false, field: '', clientId: '', broker: '', currentValue: '', clientName: '' });
  const [updatingRows, setUpdatingRows] = useState({}); // Track which rows are being updated



  // Search and sort state
  const [searchQuery, setSearchQuery] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  // Status filter state (all true by default)
  const [statusFilters, setStatusFilters] = useState({
    active: true,
    inactive: true,
    deactivated: true,
    dummy: true,
    hasMultiplier: true, // Filter for users with multiplier > 0
  });
  const [showStatusFilter, setShowStatusFilter] = useState(false);
  // ref for the status filter dropdown so we can close it on outside clicks
  const statusFilterRef = useRef(null);

  // Broker filter state
  const [brokerFilters, setBrokerFilters] = useState({});
  const [showBrokerFilter, setShowBrokerFilter] = useState(false);
  const brokerFilterRef = useRef(null);

  // Get refetch functions and ws helpers from global context
  const { refetch, reconnectWebSocket, pingWebSocket, refreshAll, wsConnected } = useGlobalData();

  const wsStatus = useWebSocketStatusStore((state) => state.status);


  const reconnectManager = () => {
    reconnectWebSocket();
    WebSocketManager.getInstance().reconnectWebSocket();
  };

  const pingWebSocketManager = () => {
    pingWebSocket();
    WebSocketManager.getInstance().pingWebSocket();
  };
  // Log when component renders with data
  useEffect(() => {
    if (!loading && data.length > 0) {
      console.log('🎨 ACCOUNT VIEW RENDERED WITH DATA at', new Date().toISOString());
      performance.mark('account-view-rendered');
    }
  }, [loading, data]);

  // Close status filter when clicking outside or pressing Escape
  useEffect(() => {
    function handleDocumentClick(e) {
      if (!showStatusFilter) return;
      const el = statusFilterRef.current;
      if (el && !el.contains(e.target)) {
        setShowStatusFilter(false);
      }
    }

    function handleEscape(e) {
      if (e.key === 'Escape' && showStatusFilter) setShowStatusFilter(false);
    }

    document.addEventListener('mousedown', handleDocumentClick);
    document.addEventListener('touchstart', handleDocumentClick);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleDocumentClick);
      document.removeEventListener('touchstart', handleDocumentClick);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [showStatusFilter]);

  // Close broker filter when clicking outside or pressing Escape
  useEffect(() => {
    function handleDocumentClick(e) {
      if (!showBrokerFilter) return;
      const el = brokerFilterRef.current;
      if (el && !el.contains(e.target)) {
        setShowBrokerFilter(false);
      }
    }

    function handleEscape(e) {
      if (e.key === 'Escape' && showBrokerFilter) setShowBrokerFilter(false);
    }

    document.addEventListener('mousedown', handleDocumentClick);
    document.addEventListener('touchstart', handleDocumentClick);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleDocumentClick);
      document.removeEventListener('touchstart', handleDocumentClick);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [showBrokerFilter]);

  // Initialize broker filters when data changes
  useEffect(() => {
    if (!data || data.length === 0) return;

    const uniqueBrokers = [...new Set(data.map(row => row.broker).filter(Boolean))].filter(
      (broker) => broker !== "dhan_ss" && broker !== "dhan_anurag"
    );
    
    setBrokerFilters(prev => {
        const initialFilters = { ...prev };
        uniqueBrokers.forEach(broker => {
            if (initialFilters[broker] === undefined) {
                initialFilters[broker] = true;
            }
        });
        return initialFilters;
    });
  }, [data]);

  // Fetch user details only when needed (when user clicks Load button)
  const fetchUserDetails = useCallback(async (clientId, broker) => {
    setLoadingStates(prev => ({ ...prev, [clientId]: true }));
    try {
      // For Dhan users (Dhan, Dhan SS, Dhan anurag), don't fetch - just set as unavailable
      const brokerLower = broker?.toLowerCase() || '';
      if (brokerLower.includes('dhan')) {
        setUserDetails(prev => ({
          ...prev,
          [clientId]: { pin: '-', totp_key: '-' }
        }));
        setLoadingStates(prev => ({ ...prev, [clientId]: false }));
        return;
      }

      // Use different endpoint based on broker
      const endpoint = brokerLower === 'nubra'
        ? `${server.godseye.baseUrl}get-nubra-user-by-id`
        : brokerLower === 'zerodha'
        ? `${server.godseye.baseUrl}get-zerodha-user-by-id`
        : `${server.godseye.baseUrl}get-fyers-user-by-id`;

      console.log(`🔑 Fetching details for ${clientId} (${broker}) from ${endpoint}`);

      const response = await apiClient.post(
        endpoint,
        { client_id: clientId },
        { headers: { 'Content-Type': 'application/json' } }
      );

      console.log(`✅ Received data for ${clientId}:`, response.data);

      setUserDetails(prev => ({ ...prev, [clientId]: response.data }));
    } catch (error) {
      console.error(`❌ Error fetching details for ${clientId}:`, error);
      setUserDetails(prev => ({ ...prev, [clientId]: { pin: 'Error', totp_key: 'Error' } }));
    } finally {
      setLoadingStates(prev => ({ ...prev, [clientId]: false }));
    }
  }, []);

  // Open edit modal
  const openEditModal = useCallback((field, clientId, broker, currentValue, clientName) => {
    // Convert field name to API format
    const apiField = field === 'cashInput' ? 'cash_input' : 'discount';
    setEditModal({ isOpen: true, field: apiField, clientId, broker, currentValue, clientName });
  }, []);

  // Close edit modal
  const closeEditModal = useCallback(() => {
    setEditModal({ isOpen: false, field: '', clientId: '', broker: '', currentValue: '', clientName: '' });
  }, []);

  // Open full edit modal - use a small timeout to force a clean remount if needed
  const openFullEditModal = useCallback((type, user) => {
    // First clear it to ensure any existing modal state is wiped
    setFullEditModal({ isOpen: false, type: null, user: null });
    
    // Then set it in the next tick
    setTimeout(() => {
        setFullEditModal({ isOpen: true, type, user });
    }, 10);
  }, []);

  // Close full edit modal
  const closeFullEditModal = useCallback(() => {
    setFullEditModal({ isOpen: false, type: null, user: null });
  }, []);


  // Wrapper for refetching users (passed to modals as fetchFyersUserList)
  const handleRefetchUsers = async () => {
    setIsRefetching(true);
    try {
      await Promise.all([
        refetch.users(),
        refetch.userMappings(),
        refetch.indexMultipliers()
      ]);
    } catch (error) {
      console.error("Failed to refetch users:", error);
      toast.error("Failed to refresh user data");
    } finally {
      setIsRefetching(false);
    }
  };

  const handleEditIndexOptionsConfig = (clientId, payload) => {
    console.log("Client Id", clientId);
    console.log("Updtaed Multipliers Payload data \n\n", payload);

    setIsRefetching(true); // Start loading immediately

    let config = {
      method: "POST",
      url: `${server.godseye.baseUrl}update-index-options-multiplier`,
      data: payload,
    };

    apiClient
      .request(config)
      .then((response) => {
        console.log("Response is ---->", response.data);

        if (response.data.status === 200) {
          toast.success("Updated Multiplier data Successfully !!", {
            duration: 5000,
          });
          // Call refetch inside the chain, but we already set loading.
          // handleRefetchUsers handles its own loading state, but since we are already "refetching" visually,
          // we can just call the refetch logic directly or call the function.
          // Calling the function is fine, it will just set loading to true again (redundant but safe) or we can await it.
          // Since handleRefetchUsers is async, we should await it if we want to clear loading AFTER it's done.
          // However, handleEditIndexOptionsConfig is not async here. 
          // Let's just call it.
          handleRefetchUsers();
        } else {
          toast.error("Failed to Update Multiplier data !!", {
            duration: 5000,
          });
          setIsRefetching(false); // Clear loading if failed
        }
      })
      .catch((error) => {
        console.log(error);
        toast.error("Error updating multiplier data");
        setIsRefetching(false); // Clear loading if error
      });
  };

  // Generic handler for adding user index multiplier
  const handleAddUserIndexMultiplier = (clientId, name, multiplier, broker, extraFields = {}) => {
    setIsRefetching(true);
    const payload = {
      client_id: clientId.trim(),
      name: name.trim(),
      broker: broker,
      indexMultiplier: parseInt(multiplier),
      ...extraFields
    };

    console.log(`Adding ${broker} user payload:`, payload);

    apiClient.post(`${server.godseye.baseUrl}add-index-options-multiplier`, payload)
      .then((response) => {
        if (response.data.status === 200) {
          toast.success("User Added Successfully !!");
          handleRefetchUsers();
        } else {
          toast.error("Failed to add User !!");
          setIsRefetching(false);
        }
      })
      .catch((error) => {
        console.error("Error adding user:", error);
        toast.error("Error adding user");
        setIsRefetching(false);
      });
  };

  const handleAddFyersUserIndexMuliplier = (client_id, name, multiplier, bankniftyMultiplier, finniftyMultiplier, midcpniftyMultiplier, sensexMultiplier) => {
    handleAddUserIndexMultiplier(client_id, name, multiplier, "fyers", {
      bankniftyMultiplier: parseInt(bankniftyMultiplier || 0),
      finniftyMultiplier: parseInt(finniftyMultiplier || 0),
      midcpniftyMultiplier: parseInt(midcpniftyMultiplier || 0),
      sensexMultiplier: parseInt(sensexMultiplier || 0),
    });
  };

  const handleAddNubraUserIndexMuliplier = (client_id, name, multiplier, bankniftyMultiplier, finniftyMultiplier, midcpniftyMultiplier, sensexMultiplier) => {
    handleAddUserIndexMultiplier(client_id, name, multiplier, "nubra", {
      bankniftyMultiplier: parseInt(bankniftyMultiplier || 0),
      finniftyMultiplier: parseInt(finniftyMultiplier || 0),
      midcpniftyMultiplier: parseInt(midcpniftyMultiplier || 0),
      sensexMultiplier: parseInt(sensexMultiplier || 0),
    });
  };

  const handleAddDhanXTSUserIndexMultiplier = (client_id, name, multiplier, bankniftyMultiplier, finniftyMultiplier, midcpniftyMultiplier, sensexMultiplier) => {
    handleAddUserIndexMultiplier(client_id, name, multiplier, "dhan_xts", {
      bankniftyMultiplier: parseInt(bankniftyMultiplier || 0),
      finniftyMultiplier: parseInt(finniftyMultiplier || 0),
      midcpniftyMultiplier: parseInt(midcpniftyMultiplier || 0),
      sensexMultiplier: parseInt(sensexMultiplier || 0),
    });
  };

  const handleAddDhanHQUserIndexMultiplier = (client_id, name, multiplier, bankniftyMultiplier, finniftyMultiplier, midcpniftyMultiplier, sensexMultiplier) => {
    handleAddUserIndexMultiplier(client_id, name, multiplier, "dhan_hq", {
      bankniftyMultiplier: parseInt(bankniftyMultiplier || 0),
      finniftyMultiplier: parseInt(finniftyMultiplier || 0),
      midcpniftyMultiplier: parseInt(midcpniftyMultiplier || 0),
      sensexMultiplier: parseInt(sensexMultiplier || 0),
    });
  };

  // Update cash input or discount
  const updateValue = async (newValue) => {
    const clientId = editModal.clientId;

    try {
      console.log('🔄 updateValue called with:', newValue);

      // Set updating state for this row
      setUpdatingRows(prev => ({ ...prev, [clientId]: true }));

      const payload = {
        client_id: clientId,
        broker: editModal.broker,
        field: editModal.field,
        value: parseFloat(String(newValue))
      };

      console.log('📤 Updating value:', payload);

      const response = await apiClient.post(
        `${server.godseye.baseUrl}update-cash-input-discount`,
        payload,
        { headers: { 'Content-Type': 'application/json' } }
      );

      console.log('✅ Update successful:', response.data);

      closeEditModal();

      // Refresh data to show updated values
      console.log('🔄 Refreshing data...');
      await Promise.all([
        refetch.users(),
        refetch.userMappings(),
        refetch.indexMultipliers()
      ]);
      console.log('✅ Data refreshed');

      // Show success toast AFTER everything is complete
      const fieldLabel = editModal.field === 'cash_input' ? 'Cash Input' : 'Discount';
      toast.success(`${fieldLabel} updated successfully!`);
    } catch (error) {
      console.error('❌ Error updating value:', error);

      // Show error toast
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to update value';
      toast.error(`Error: ${errorMessage}`);
    } finally {
      // Clear updating state for this row
      setUpdatingRows(prev => {
        const newState = { ...prev };
        delete newState[clientId];
        return newState;
      });
    }
  };

  // Filter data based on search query
  const filterData = (dataToFilter) => {
    let filtered = dataToFilter;

    // Apply status filters
    const activeStatuses = Object.keys(statusFilters).filter(k => k !== 'hasMultiplier' && statusFilters[k]);
    if (activeStatuses.length > 0) {
      filtered = filtered.filter(row => activeStatuses.includes(String(row.status)));
    }

    // Apply hasMultiplier filter (AND condition with status)
    if (statusFilters.hasMultiplier) {
      // Only show users with totalMul > 0
      filtered = filtered.filter(row => (row.totalMul || 0) > 0);
    }

    // Apply broker filters
    const activeBrokers = Object.keys(brokerFilters).filter(k => brokerFilters[k]);
    if (activeBrokers.length > 0 && Object.keys(brokerFilters).length > 0) {
      filtered = filtered.filter(row => activeBrokers.includes(row.broker));
    }

    // Then apply search query (if any)
    if (!searchQuery.trim()) return filtered;

    const query = searchQuery.toLowerCase();
    return filtered.filter(row => {
      const name = (row.name || '').toLowerCase();
      const clientId = (row.clientId || '').toLowerCase();
      const broker = (row.broker || '').toLowerCase();

      // Check for broker-specific search terms
      const brokerMatch =
        broker.includes(query) ||
        (broker === 'dhan_xts' && (query === 'dx' || query.includes('dhan xts') || query.includes('dhan_xts')));

      return name.includes(query) || clientId.includes(query) || brokerMatch;
    });
  };

  // Sort data based on column
  const sortData = (dataToSort) => {
    if (!sortConfig.key) return dataToSort;

    const sorted = [...dataToSort].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      // Handle null/undefined values
      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      // Compare values
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }

      // String comparison
      const aStr = String(aValue).toLowerCase();
      const bStr = String(bValue).toLowerCase();

      if (aStr < bStr) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aStr > bStr) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  };

  // Process data with filtering and sorting - memoized to prevent lag
  const processedData = useMemo(() => {
    return sortData(filterData(data));
  }, [data, searchQuery, sortConfig, statusFilters, brokerFilters]);

  // Handle sort click - memoized
  const handleSort = useCallback((key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  }, []);

  // Get sort icon - memoized
  const getSortIcon = useCallback((key) => {
    if (sortConfig.key !== key) {
      return (
        <svg className="w-3 h-3 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      );
    }

    if (sortConfig.direction === 'asc') {
      return (
        <svg className="w-3 h-3 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
        </svg>
      );
    }

    return (
      <svg className="w-3 h-3 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    );
  }, [sortConfig]);

  const tabs = [
    { id: 'details', label: 'User Details Info', count: data?.length || 0 },
    { id: 'multipliers', label: 'User Multipliers Info', count: data?.length || 0 },
  ];

  if (loading) {
    return (
      <div className="bg-[#1a1d29] p-6 flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <svg className="inline-block animate-spin h-12 w-12 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="text-gray-400 mt-4">Loading account data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#1a1d29] min-h-screen">
      {/* Sub-tabs and Search Bar - Sticky */}
      <div className="sticky top-0 z-40 bg-[#14161f] border-b border-gray-700/50 shadow-lg flex items-center justify-between px-6 py-2">
        {/* Tabs */}
        <div className="flex space-x-1 bg-[#0f1116] p-1 rounded-lg border border-gray-700">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-1.5 rounded-md text-sm font-medium transition-all duration-200 ${
                activeTab === tab.id
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'text-gray-400 hover:text-gray-200 hover:bg-[#1a1d29]'
              }`}
            >
              {tab.label}
              <span className={`px-2 py-0.5 rounded-full text-xs ${
                activeTab === tab.id 
                  ? 'bg-blue-500/50 text-blue-100' 
                  : 'bg-gray-800 text-gray-400'
              }`}>
                {tab.count}
              </span>
            </button>
          ))}
        </div>

        <div className="flex items-center justify-end gap-4">
          
          {/* Add New User Modal */}
          <AddNewUserModal
            handleAddFyersUserIndexMuliplier={handleAddFyersUserIndexMuliplier}
            handleAddNubraUserIndexMuliplier={handleAddNubraUserIndexMuliplier}
            handleAddDhanXTSUserIndexMultiplier={handleAddDhanXTSUserIndexMultiplier}
            handleAddDhanHQUserIndexMultiplier={handleAddDhanHQUserIndexMultiplier}
          />

          {/* Search Input */}
          <div className="relative">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name or client ID..."
              className="bg-[#0f1116] text-white text-sm px-4 py-1.5 pl-9 pr-4 rounded-md border border-gray-700 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 w-64"
            />
            <svg
              className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>

          {/* Status filter dropdown */}
          <div className="relative" ref={statusFilterRef}>
            <button
              onClick={() => setShowStatusFilter(s => !s)}
              className="flex items-center gap-2 px-3 py-1 bg-[#0f1116] text-sm rounded border border-gray-700 text-gray-300 hover:bg-[#14161f]"
              title="Filter by Status"
            >
              <span>Filter by Status</span>
              <svg className="w-3 h-3 text-gray-400" viewBox="0 0 20 20" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7l3-3 3 3m0 6l-3 3-3-3" />
              </svg>
            </button>

            {showStatusFilter && (
              <div className="absolute mt-2 right-0 w-48 bg-[#14161f] border border-gray-700 rounded shadow-lg p-3 z-50">
                {/* Status checkboxes */}
                {['active', 'inactive', 'deactivated', 'dummy'].map(statusKey => (
                  <label key={statusKey} className="flex items-center gap-2 p-1 cursor-pointer hover:bg-[#0f1116] rounded">
                    <input
                      type="checkbox"
                      checked={!!statusFilters[statusKey]}
                      onChange={() => setStatusFilters(prev => ({ ...prev, [statusKey]: !prev[statusKey] }))}
                      className="w-4 h-4 accent-emerald-400"
                    />
                    <span className="text-sm text-gray-300 capitalize">{statusKey}</span>
                  </label>
                ))}

                {/* Separator */}
                <div className="border-t border-gray-700 my-2"></div>

                {/* Multiplier filter */}
                <label className="flex items-center gap-2 p-1 cursor-pointer hover:bg-[#0f1116] rounded">
                  <input
                    type="checkbox"
                    checked={!!statusFilters.hasMultiplier}
                    onChange={() => setStatusFilters(prev => ({ ...prev, hasMultiplier: !prev.hasMultiplier }))}
                    className="w-4 h-4 accent-blue-400"
                  />
                  <span className="text-sm text-gray-300">Multiplier &gt; 0</span>
                </label>
              </div>
            )}
          </div>

          {/* Broker filter dropdown */}
          <div className="relative" ref={brokerFilterRef}>
            <button
              onClick={() => setShowBrokerFilter(s => !s)}
              className="flex items-center gap-2 px-3 py-1 bg-[#0f1116] text-sm rounded border border-gray-700 text-gray-300 hover:bg-[#14161f]"
              title="Filter by Broker"
            >
              <span>Filter by Broker</span>
              <svg className="w-3 h-3 text-gray-400" viewBox="0 0 20 20" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7l3-3 3 3m0 6l-3 3-3-3" />
              </svg>
            </button>

            {showBrokerFilter && Object.keys(brokerFilters).length > 0 && (
              <div className="absolute mt-2 right-0 w-48 bg-[#14161f] border border-gray-700 rounded shadow-lg z-50">
                {/* Select All / Clear All buttons */}
                <div className="flex items-center justify-between gap-2 p-2 border-b border-gray-700">
                  <button
                    onClick={() => {
                      const allEnabled = {};
                      Object.keys(brokerFilters).forEach(key => {
                        allEnabled[key] = true;
                      });
                      setBrokerFilters(allEnabled);
                    }}
                    className="flex-1 px-2 py-1 text-xs bg-blue-600 hover:bg-blue-500 text-white rounded transition-colors"
                  >
                    Select All
                  </button>
                  <button
                    onClick={() => {
                      const allDisabled = {};
                      Object.keys(brokerFilters).forEach(key => {
                        allDisabled[key] = false;
                      });
                      setBrokerFilters(allDisabled);
                    }}
                    className="flex-1 px-2 py-1 text-xs bg-gray-600 hover:bg-gray-500 text-white rounded transition-colors"
                  >
                    Clear All
                  </button>
                </div>

                {/* Broker checkboxes */}
                <div className="p-3 max-h-64 overflow-y-auto">
                  {Object.keys(brokerFilters).sort().map(brokerKey => (
                    <label key={brokerKey} className="flex items-center gap-2 p-1 cursor-pointer hover:bg-[#0f1116] rounded">
                      <input
                        type="checkbox"
                        checked={!!brokerFilters[brokerKey]}
                        onChange={() => setBrokerFilters(prev => ({ ...prev, [brokerKey]: !prev[brokerKey] }))}
                        className="w-4 h-4 accent-blue-400"
                      />
                      <span className="text-sm text-gray-300">{formatBrokerName(brokerKey)}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Bot Status */}
          <div className="flex items-center gap-2 bg-[#0f1116] px-2 py-1 rounded border border-gray-700">
            <span className={`w-2 h-2 rounded-full ${wsStatus === "open" ? 'bg-emerald-400' : 'bg-red-500'}`}></span>
            <span className="text-gray-300 text-sm whitespace-nowrap">Bot: {wsStatus === "open" ? 'ACTIVE' : 'DISCONNECTED'}</span>
          </div>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'details' && (
        <>
          <UserDetailsTable
            data={processedData}
            userDetails={userDetails}
            fetchUserDetails={fetchUserDetails}
            loadingStates={loadingStates}
            updatingRows={updatingRows}
            openEditModal={openEditModal}
            handleSort={handleSort}
            getSortIcon={getSortIcon}
            handleRefetchUsers={handleRefetchUsers}
            openFullEditModal={openFullEditModal}
          />
          <EditModal
            isOpen={editModal.isOpen}
            field={editModal.field}
            clientId={editModal.clientId}
            clientName={editModal.clientName}
            currentValue={editModal.currentValue}
            onClose={closeEditModal}
            onUpdate={updateValue}
          />
        </>
      )}

      {activeTab === 'multipliers' && (
        <UserMultipliersTable
          data={processedData}
          handleSort={handleSort}
          getSortIcon={getSortIcon}
          handleEditIndexOptionsConfig={handleEditIndexOptionsConfig}
        />
      )}

      {/* Full Edit Modals - rendered ONCE outside the table, not per-row */}
      {/* We wrap them in a hidden div because these modals have their own DialogTriggers (buttons) that we want to hide, 
          since we are triggering them programmatically from the table rows. */}
      <div style={{ display: 'none' }}>
        {fullEditModal.isOpen && fullEditModal.type?.includes('fyers') && (
          <EditFyersUserModal
            key={`fyers_${fullEditModal.user?.client_id}`}
            userDetails={fullEditModal.user}
            fetchFyersUserList={handleRefetchUsers}
            forceOpen={true}
            onForceClose={closeFullEditModal}
          />
        )}
        {fullEditModal.isOpen && fullEditModal.type?.includes('nubra') && (
          <EditNubraUserModal
            key={`nubra_${fullEditModal.user?.client_id}`}
            userDetails={fullEditModal.user}
            fetchFyersUserList={handleRefetchUsers}
            forceOpen={true}
            onForceClose={closeFullEditModal}
          />
        )}
        {fullEditModal.isOpen && (fullEditModal.type === 'dhan_hq' || fullEditModal.type === 'dhan') && (
          <EditDhanHQUserModal
            key={`dhan_hq_${fullEditModal.user?.client_id}`}
            userDetails={fullEditModal.user}
            fetchFyersUserList={handleRefetchUsers}
            forceOpen={true}
            onForceClose={closeFullEditModal}
          />
        )}
        {fullEditModal.isOpen && fullEditModal.type === 'dhan_xts' && (
          <EditDhanXTSUserModal
            key={`dhan_xts_${fullEditModal.user?.client_id}`}
            userDetails={fullEditModal.user}
            fetchFyersUserList={handleRefetchUsers}
            forceOpen={true}
            onForceClose={closeFullEditModal}
          />
        )}
      </div>

      {/* Loading Overlay */}
      {isRefetching && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-[#1a1d29] p-4 rounded-xl border border-gray-700 shadow-xl flex flex-col items-center gap-3">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            <span className="text-gray-200 font-medium text-sm">Updating Data...</span>
          </div>
        </div>
      )}
      
    </div>
  );
}

/**
 * User Details Info Table
 */
const UserRow = React.memo(({ 
    row, 
    idx, 
    details, 
    isLoading, 
    isUpdating, 
    fetchUserDetails, 
    copyToClipboard, 
    renderBrokerBadges, 
    formatNumber, 
    openEditModal, 
    openFullEditModal 
}) => {
    return (
        <tr
            key={`${row.clientId}_${row.broker}`}
            className={`border-b border-gray-800/50 hover:bg-[#14161f] transition-colors relative ${isUpdating ? 'opacity-60' : ''}`}
        >
            {isUpdating && (
                <td colSpan="20" className="absolute inset-0 pointer-events-none z-50">
                    <div className="flex items-center justify-center h-full w-full bg-[#1a1d29]/40 backdrop-blur-[1px]">
                         <div className="flex items-center gap-2 bg-[#1a1d29] px-3 py-1 rounded-full border border-blue-500/30 shadow-lg pointer-events-auto">
                            <svg className="animate-spin h-4 w-4 text-blue-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            <span className="text-blue-400 text-xs font-semibold">Updating...</span>
                        </div>
                    </div>
                </td>
            )}

            <td className="py-2 px-4 text-center text-gray-400">
                {idx + 1}
            </td>
            <td className="py-2 px-4 text-white font-medium">
                {row.name || '-'}
            </td>
            <td className="py-2 px-4 text-gray-300">
                <div className="flex items-center justify-between gap-2">
                    <span>{row.clientId}</span>
                    <button
                        onClick={() => copyToClipboard(row.clientId, 'Client ID')}
                        className="p-1 text-xs text-gray-400 hover:text-blue-400 hover:bg-blue-500/10 rounded transition-colors cursor-pointer"
                        title="Copy Client ID"
                    >
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                    </button>
                </div>
            </td>
            <td className="py-2 px-4">
                <div className="flex items-center justify-between gap-2">
                    {isLoading ? (
                        <div className="flex items-center gap-2">
                            <svg className="animate-spin h-3 w-3 text-blue-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            <span className="text-gray-400 text-xs">Loading...</span>
                        </div>
                    ) : details && details.pin && details.pin !== '-' ? (
                        <>
                            <span className="text-gray-300">{details.pin}</span>
                            <button
                                onClick={() => copyToClipboard(details.pin, 'PIN')}
                                className="p-1 text-xs text-gray-400 hover:text-blue-400 hover:bg-blue-500/10 rounded transition-colors cursor-pointer"
                                title="Copy PIN"
                            >
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                </svg>
                            </button>
                        </>
                    ) : (
                        (row.broker?.toLowerCase() || '').includes('dhan') ? (
                            <span className="text-gray-500">-</span>
                        ) : (
                            <button
                                onClick={() => fetchUserDetails(row.clientId, row.broker)}
                                className="px-2 py-1 text-xs font-medium bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 border border-blue-500/30 rounded cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                disabled={isLoading}
                            >
                                Fetch
                            </button>
                        )
                    )}
                </div>
            </td>
            <td className="py-2 px-4">
                <div className="flex items-center justify-between gap-2">
                    {isLoading ? (
                        <div className="flex items-center gap-2">
                            <svg className="animate-spin h-3 w-3 text-blue-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            <span className="text-gray-400 text-xs">Loading...</span>
                        </div>
                    ) : details && details.totp_code && details.totp_code !== '-' ? (
                        <>
                            <span className="text-gray-300 font-mono text-sm">{details.totp_code}</span>
                            <div className="flex items-center gap-1">
                                <button
                                    onClick={() => copyToClipboard(details.totp_code, 'TOTP Code')}
                                    className="p-1 text-xs text-gray-400 hover:text-blue-400 hover:bg-blue-500/10 rounded transition-colors cursor-pointer"
                                    title="Copy TOTP Code"
                                >
                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                    </svg>
                                </button>
                                <button
                                    onClick={() => fetchUserDetails(row.clientId, row.broker)}
                                    className="p-1 text-xs text-gray-400 hover:text-blue-400 hover:bg-blue-500/10 rounded transition-colors cursor-pointer"
                                    title="Refresh TOTP"
                                    disabled={isLoading}
                                >
                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                    </svg>
                                </button>
                            </div>
                        </>
                    ) : (
                        (row.broker?.toLowerCase() || '').includes('dhan') ? (
                            <span className="text-gray-500">-</span>
                        ) : (
                            <button
                                onClick={() => fetchUserDetails(row.clientId, row.broker)}
                                className="px-2 py-1 text-xs font-medium bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 border border-blue-500/30 rounded cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                disabled={isLoading}
                            >
                                Fetch
                            </button>
                        )
                    )}
                </div>
            </td>
            <td className="py-2 px-4 min-w-[160px] whitespace-nowrap">
                {renderBrokerBadges(row.broker)}
            </td>
            <td className="py-2 px-4 text-center">
                <span className={`px-2 py-1 rounded text-xs font-medium ${row.status === 'active'
                    ? 'bg-green-500/20 text-green-400'
                    : row.status === 'inactive'
                        ? 'bg-yellow-500/20 text-yellow-400'
                        : 'bg-red-500/20 text-red-400'
                    }`}>
                    {row.status || 'N/A'}
                </span>
            </td>
            <td className="py-2 px-4 text-right text-gray-300">
                ₹{formatNumber(row.cashAvailable)}
            </td>
            <td className="py-2 px-4 text-right">
                <div className="flex items-center justify-end gap-2">
                    <span className="text-gray-300">₹{formatNumber(row.cashInput)}</span>
                    <button
                        onClick={() => openEditModal('cashInput', row.clientId, row.broker, row.cashInput, row.name)}
                        className="p-1 text-xs text-gray-400 hover:text-blue-400 hover:bg-blue-500/10 rounded transition-colors cursor-pointer"
                        title="Edit Cash Input"
                    >
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                    </button>
                </div>
            </td>
            <td className="py-2 px-4 text-right text-gray-300">
                ₹{formatNumber(row.openValue)}
            </td>
            <td className="py-2 px-4 text-right">
                <div className="flex items-center justify-end gap-2">
                    <span className="text-gray-300">{row.discountInput}</span>
                    <button
                        onClick={() => openEditModal('discount', row.clientId, row.broker, row.discountInput, row.name)}
                        className="p-1 text-xs text-gray-400 hover:text-blue-400 hover:bg-blue-500/10 rounded transition-colors cursor-pointer"
                        title="Edit Discount"
                    >
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                    </button>
                </div>
            </td>
            <td className="py-2 px-4 text-gray-300">
                -
            </td>
            <td className="py-2 px-4 text-center">
                <span className={`px-2 py-1 rounded text-xs font-medium ${row.tokenGenerated === 'Yes'
                    ? 'bg-green-500/20 text-green-400'
                    : 'bg-red-500/20 text-red-400'
                    }`}>
                    {row.tokenGenerated}
                </span>
            </td>

            <td className="py-2 px-4 text-center">
                {row.proxyToggle ? (
                    <span className="text-green-500 text-xs font-semibold px-2 py-1 bg-green-500/10 rounded-full">
                        Active
                    </span>
                ) : (
                    <span className="text-gray-400 text-xs font-semibold px-2 py-1 bg-gray-500/10 rounded-full">
                        Disabled
                    </span>
                )}
            </td>

            <td className="py-3 px-4 text-center">
                <div className="flex items-center justify-center gap-2">
                    <button
                        onClick={() => openFullEditModal(row.broker?.toLowerCase(), { client_id: row.clientId, broker: row.broker })}
                        className="p-1.5 text-gray-400 hover:text-blue-400 hover:bg-blue-500/10 rounded transition-colors cursor-pointer"
                        title="Edit User"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                    </button>
                </div>
            </td>
        </tr>
    );
});

/**
 * Helper components for sortable headers - defined outside to prevent remounting
 */
const SortableHeader = ({ label, sortKey, handleSort, getSortIcon }) => (
    <th
        className="text-left py-3 px-4 text-gray-300 font-medium cursor-pointer hover:bg-[#1a1d29] transition-colors select-none border-b border-gray-700/50"
        onClick={() => handleSort(sortKey)}
    >
        <div className="flex items-center gap-1">
            <span>{label}</span>
            {getSortIcon(sortKey)}
        </div>
    </th>
);

const SortableHeaderRight = ({ label, sortKey, handleSort, getSortIcon }) => (
    <th
        className="text-right py-3 px-4 text-gray-300 font-medium cursor-pointer hover:bg-[#1a1d29] transition-colors select-none border-b border-gray-700/50"
        onClick={() => handleSort(sortKey)}
    >
        <div className="flex items-center justify-end gap-1">
            <span>{label}</span>
            {getSortIcon(sortKey)}
        </div>
    </th>
);

const SortableHeaderCenter = ({ label, sortKey, handleSort, getSortIcon }) => (
    <th
        className="text-center py-3 px-4 text-gray-300 font-medium cursor-pointer hover:bg-[#1a1d29] transition-colors select-none border-b border-gray-700/50"
        onClick={() => handleSort(sortKey)}
    >
        <div className="flex items-center justify-center gap-1">
            <span>{label}</span>
            {getSortIcon(sortKey)}
        </div>
    </th>
);

const UserDetailsTable = React.memo(({ data, userDetails, fetchUserDetails, loadingStates, updatingRows, openEditModal, handleSort, getSortIcon, handleRefetchUsers, openFullEditModal }) => {
    if (!data || data.length === 0) {
        return (
            <div className="p-6">
                <p className="text-gray-400">No user data available</p>
            </div>
        );
    }

    return (
        <div className="bg-[#1a1d29]">
            <div className="overflow-x-auto">

                <table className="w-full text-sm border-separate border-spacing-0">
                    <thead className="bg-[#14161f] z-[10] shadow-md">
                        <tr className="">
                            <th className="text-center py-3 px-4 text-gray-300 font-medium border-b border-gray-700/50">S.No</th>
                            <SortableHeader label="Client Name" sortKey="name" handleSort={handleSort} getSortIcon={getSortIcon} />
                            <SortableHeader label="Client ID" sortKey="clientId" handleSort={handleSort} getSortIcon={getSortIcon} />
                            <th className="text-left py-3 px-4 text-gray-300 font-medium border-b border-gray-700/50">PIN</th>
                            <th className="text-left py-3 px-4 text-gray-300 font-medium border-b border-gray-700/50">TOTP Code</th>
                            <th
                                className="text-left py-3 px-4 text-gray-300 font-medium cursor-pointer hover:bg-[#1a1d29] transition-colors select-none min-w-[160px] border-b border-gray-700/50"
                                onClick={() => handleSort('broker')}
                            >
                                <div className="flex items-center gap-1">
                                    <span>Broker</span>
                                    {getSortIcon('broker')}
                                </div>
                            </th>
                            <SortableHeaderCenter label="Status" sortKey="status" handleSort={handleSort} getSortIcon={getSortIcon} />
                            <SortableHeaderRight label="Cash Available" sortKey="cashAvailable" handleSort={handleSort} getSortIcon={getSortIcon} />
                            <SortableHeaderRight label="Cash Input" sortKey="cashInput" handleSort={handleSort} getSortIcon={getSortIcon} />
                            <SortableHeaderRight label="Open Value" sortKey="openValue" handleSort={handleSort} getSortIcon={getSortIcon} />
                            <SortableHeaderRight label="Discount" sortKey="discountInput" handleSort={handleSort} getSortIcon={getSortIcon} />
                            <SortableHeader label="Flag" sortKey="flag" handleSort={handleSort} getSortIcon={getSortIcon} />
                            <SortableHeaderCenter label="Token Generated" sortKey="tokenGenerated" handleSort={handleSort} getSortIcon={getSortIcon} />
                            <SortableHeaderCenter label="Proxy" sortKey="proxyToggle" handleSort={handleSort} getSortIcon={getSortIcon} />
                            <th className="text-center py-3 px-4 text-gray-300 font-medium border-b border-gray-700/50">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.map((row, idx) => (
                            <UserRow 
                                key={`${row.clientId}_${row.broker}`}
                                row={row}
                                idx={idx}
                                details={userDetails[row.clientId]}
                                isLoading={loadingStates[row.clientId]}
                                isUpdating={updatingRows[row.clientId]}
                                fetchUserDetails={fetchUserDetails}
                                copyToClipboard={copyToClipboard}
                                renderBrokerBadges={renderBrokerBadges}
                                formatNumber={formatNumber}
                                openEditModal={openEditModal}
                                openFullEditModal={openFullEditModal}
                            />
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
});

/**
 * User Multipliers Info Table
 */
function UserMultipliersTable({ data, handleSort, getSortIcon, handleEditIndexOptionsConfig }) {
  console.log('📊 Multipliers data:', data);

  // Multiplier columns config (stock column removed)
  const multiplierCols = [
    { label: 'Nifty', key: 'niftyMul', sortKey: 'niftyMul' },
    { label: 'BankNifty', key: 'bnfMul', sortKey: 'bnfMul' },
    { label: 'FinNifty', key: 'finniftyMul', sortKey: 'finniftyMul' },
    { label: 'MCX', key: 'mcx', sortKey: 'mcxMul' },
    { label: 'MidcpNifty', key: 'midcpniftyMul', sortKey: 'midcpniftyMul' },
    { label: 'Sensex', key: 'sensexMul', sortKey: 'sensexMul' }
  ];

  // Column filter state: key -> Set(selected values)
  const [columnFilters, setColumnFilters] = React.useState({});
  const [openFilter, setOpenFilter] = React.useState(null); // which column key has dropdown open
  const wrapperRef = React.useRef(null);

  // Compute available options for each column from data
  const optionsMap = React.useMemo(() => {
    const out = {};
    if (!Array.isArray(data)) return out;
    multiplierCols.forEach(col => {
      const values = new Set();
      data.forEach(r => {
        const v = col.key === 'mcx' ? (r.mcx_multiplier ?? r.mcxMul ?? 0) : (r[col.key] ?? 0);
        values.add(v);
      });
      out[col.key] = Array.from(values).sort((a, b) => a - b);
    });
    return out;
  }, [data]);

  // Initialize filters when data changes: select all unique values for each column by default
  React.useEffect(() => {
    const next = {};
    Object.keys(optionsMap).forEach(k => next[k] = new Set(optionsMap[k]));
    setColumnFilters(next);
  }, [optionsMap]);

  React.useEffect(() => {
    const onDoc = (e) => {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(e.target)) {
        setOpenFilter(null);
      }
    };
    const onEsc = (e) => { if (e.key === 'Escape') setOpenFilter(null); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('touchstart', onDoc);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('touchstart', onDoc);
      document.removeEventListener('keydown', onEsc);
    };
  }, []);

  const toggleFilterValue = (colKey, val) => {
    setColumnFilters(prev => {
      const prevSet = prev[colKey] ? new Set(prev[colKey]) : new Set();
      if (prevSet.has(val)) prevSet.delete(val); else prevSet.add(val);
      return { ...prev, [colKey]: prevSet };
    });
  };

  const selectAllForCol = (colKey) => {
    const values = new Set(optionsMap[colKey] || []);
    setColumnFilters(prev => ({ ...prev, [colKey]: new Set(Array.from(values)) }));
    // Small delay to make the close smooth
    setTimeout(() => setOpenFilter(null), 100);
  };

  const clearFilterForCol = (colKey) => {
    // When clearing, select all values instead of making it empty
    const values = new Set(optionsMap[colKey] || []);
    setColumnFilters(prev => ({ ...prev, [colKey]: values }));
    // Small delay to make the close smooth
    setTimeout(() => setOpenFilter(null), 100);
  };

  // Apply all active column filters to data
  const filteredData = React.useMemo(() => {
    if (!Array.isArray(data)) return [];
    return data.filter(row => {
      for (const col of multiplierCols) {
        const sel = columnFilters[col.key];
        if (!sel || sel.size === 0) continue; // no filter defined or empty = show all
        const v = col.key === 'mcx' ? (row.mcx_multiplier ?? row.mcxMul ?? 0) : (row[col.key] ?? 0);
        if (!sel.has(v)) return false;
      }
      return true;
    });
  }, [data, columnFilters]);

  // Helper components for sortable headers
  const SortableHeader = ({ label, sortKey }) => (
    <th
      className="text-left py-3 px-4 text-gray-300 font-medium cursor-pointer hover:bg-[#1a1d29] transition-colors select-none"
      onClick={() => handleSort(sortKey)}
    >
      <div className="flex items-center gap-1">
        <span>{label}</span>
        {getSortIcon(sortKey)}
      </div>
    </th>
  );

  const SortableHeaderCenter = ({ label, sortKey }) => (
    <th
      className="text-center py-3 px-4 text-gray-300 font-medium cursor-pointer hover:bg-[#1a1d29] transition-colors select-none"
      onClick={() => handleSort(sortKey)}
    >
      <div className="flex items-center justify-center gap-1">
        <span>{label}</span>
        {getSortIcon(sortKey)}
      </div>
    </th>
  );

  // Early return after all hooks are declared
  if (!data || data.length === 0) {
    return (
      <div className="p-6">
        <p className="text-gray-400">No multiplier data available</p>
      </div>
    );
  }

  return (
    <div className="bg-[#1a1d29] pb-10" ref={wrapperRef}>
      <div className="overflow-x-auto max-h-[calc(100vh-160px)] relative custom-scroll">

        <table className="w-full text-sm border-separate border-spacing-0">
          <thead className="sticky top-0 bg-[#14161f] z-[10] shadow-md">
            <tr className="border-b border-gray-700/50">
              <th className="text-center py-3 px-4 text-gray-300 font-medium">S.No</th>
              <SortableHeader label="Client Name" sortKey="name" />
              <SortableHeader label="Client ID" sortKey="clientId" />
              <th
                className="text-left py-3 px-4 text-gray-300 font-medium cursor-pointer hover:bg-[#1a1d29] transition-colors select-none min-w-[160px]"
                onClick={() => handleSort('broker')}
              >
                <div className="flex items-center gap-1">
                  <span>Broker</span>
                  {getSortIcon('broker')}
                </div>
              </th>
              {multiplierCols.map(col => (
                <th
                  key={col.key}
                  className="relative text-center py-3 px-4 text-gray-300 font-medium hover:bg-[#1a1d29] transition-colors select-none"
                >
                  <div className="flex items-center justify-center gap-1.5">
                    <span
                      className="cursor-pointer"
                      onClick={() => handleSort(col.sortKey)}
                    >
                      {col.label}
                    </span>
                    <span
                      className="cursor-pointer"
                      onClick={() => handleSort(col.sortKey)}
                    >
                      {getSortIcon(col.sortKey)}
                    </span>

                    {/* inline filter icon next to sort icon */}
                    <button
                      onClick={(e) => { e.stopPropagation(); setOpenFilter(openFilter === col.key ? null : col.key); }}
                      className={`p-0.5 rounded transition-colors ${columnFilters[col.key] && columnFilters[col.key].size !== (optionsMap[col.key] || []).length
                        ? 'text-emerald-400 hover:text-emerald-300'
                        : 'text-gray-500 hover:text-gray-300'
                        }`}
                      title={`Filter ${col.label}`}
                    >
                      <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2a1 1 0 01-.293.707L14 13.414V19a1 1 0 01-.447.895l-4 2A1 1 0 018 21v-7.586L3.293 6.707A1 1 0 013 6V4z" />
                      </svg>
                    </button>
                  </div>

                  {openFilter === col.key && (
                    <div
                      className="absolute z-30 top-full left-1/2 -translate-x-1/2 mt-2 w-56 bg-[#252937] border border-gray-600 rounded-lg shadow-2xl overflow-hidden"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {/* Header with count */}
                      <div className="px-4 py-3 bg-[#1e2230] border-b border-gray-600">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-gray-300">
                            {columnFilters[col.key] ? columnFilters[col.key].size : 0} of {(optionsMap[col.key] || []).length} selected
                          </span>
                          <button
                            onClick={() => setOpenFilter(null)}
                            className="text-gray-400 hover:text-white transition-colors"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                        <div className="flex gap-2">
                          <button
                            className="flex-1 px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded transition-colors"
                            onClick={() => selectAllForCol(col.key)}
                          >
                            Select All
                          </button>
                          <button
                            className="flex-1 px-3 py-1.5 text-xs font-medium text-white bg-gray-600 hover:bg-gray-700 rounded transition-colors"
                            onClick={() => clearFilterForCol(col.key)}
                          >
                            Clear Filter
                          </button>
                        </div>
                      </div>

                      {/* Scrollable list */}
                      <div className="max-h-64 overflow-y-auto" style={{
                        scrollbarWidth: 'thin',
                        scrollbarColor: '#4B5563 #1F2937'
                      }}>
                        {(optionsMap[col.key] || []).map((v) => (
                          <label
                            key={String(v)}
                            className="flex items-center gap-2 px-4 py-2.5 text-gray-200 hover:bg-[#1e2230] cursor-pointer transition-colors border-b border-gray-700/30 last:border-b-0"
                          >
                            <input
                              type="checkbox"
                              checked={!!(columnFilters[col.key] && columnFilters[col.key].has(v))}
                              onChange={() => toggleFilterValue(col.key, v)}
                              className="w-4 h-4 rounded border-gray-500 text-blue-600 focus:ring-2 focus:ring-blue-500 focus:ring-offset-0 bg-[#1a1d29] cursor-pointer accent-blue-600"
                            />
                            <span className="text-sm font-medium">{v}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                </th>
              ))}
              <SortableHeaderCenter label="Total" sortKey="totalMul" />
              <th className="text-center py-3 px-4 text-gray-300 font-medium">Action</th>
            </tr>
          </thead>
          <tbody>
            {filteredData.map((row, idx) => {
              return (
                <tr
                  key={`${row.clientId}_${row.broker}` || idx}
                  className="border-b border-gray-800/50 hover:bg-[#14161f] transition-colors"
                >
                  <td className="py-2 px-4 text-center text-gray-400">
                    {idx + 1}
                  </td>
                  <td className="py-2 px-4 text-white font-medium">
                    {row.name || '-'}
                  </td>
                  <td className="py-2 px-4 text-gray-300">
                    {row.clientId || '-'}
                  </td>
                  <td className="py-2 px-4 min-w-[160px] whitespace-nowrap">
                    {renderBrokerBadges(row.broker)}
                  </td>
                  <td className={mulCellClass(row.niftyMul)}>
                    {row.niftyMul ?? 0}
                  </td>
                  <td className={mulCellClass(row.bnfMul)}>
                    {row.bnfMul ?? 0}
                  </td>
                  <td className={mulCellClass(row.finniftyMul)}>
                    {row.finniftyMul ?? 0}
                  </td>
                  <td className={mulCellClass(row.mcx_multiplier ?? row.mcxMul ?? 0)}>
                    {row.mcx_multiplier ?? row.mcxMul ?? 0}
                  </td>
                  <td className={mulCellClass(row.midcpniftyMul)}>
                    {row.midcpniftyMul ?? 0}
                  </td>
                  <td className={mulCellClass(row.sensexMul)}>
                    {row.sensexMul ?? 0}
                  </td>
                  <td className="py-2 px-4 text-center text-gray-300 font-semibold">
                    {row.totalMul ?? 0}
                  </td>
                  <td className="py-2 px-4 text-center">
                    <div className="transform scale-90">
                      <EditIndexOptionsModal
                        clientId={row.clientId}
                        broker={row.broker}
                        prevMultiplier={row.niftyMul ?? 0}
                        prevBankNiftyMultiplier={row.bnfMul ?? 0}
                        prevFinNiftyMultiplier={row.finniftyMul ?? 0}
                        prevMidcpNiftyMultiplier={row.midcpniftyMul ?? 0}
                        prevSensexMultiplier={row.sensexMul ?? 0}
                        prevMcxMultiplier={row.mcx_multiplier ?? row.mcxMul ?? 0}
                        handleEditIndexOptionsConfig={handleEditIndexOptionsConfig}
                      />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * Edit Modal Component
 */
function EditModal({ isOpen, field, clientId, clientName, currentValue, onClose, onUpdate }) {
  const [value, setValue] = useState('');

  // Update value when modal opens
  useEffect(() => {
    if (isOpen) {
      console.log('📝 Modal opened with:', { field, clientId, currentValue });
      setValue(currentValue?.toString() || '');
    }
  }, [isOpen, currentValue, field, clientId]);

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('🚀 Form submitted with value:', value);
    onUpdate(value);
  };

  if (!isOpen) return null;

  const fieldLabel = field === 'cash_input' ? 'Cash Input' : 'Discount';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-[#1a1d29] border border-gray-700 rounded-lg p-6 w-full max-w-md">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white">Edit {fieldLabel}</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="mb-4">
          <p className="text-sm text-gray-400 mb-1">Client: <span className="text-white">{clientName}</span></p>
          <p className="text-sm text-gray-400 mb-1">Client ID: <span className="text-white">{clientId}</span></p>
          <p className="text-sm text-gray-400">Current Value: <span className="text-white font-semibold">{currentValue || '-'}</span></p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-300 mb-2">
              New {fieldLabel}
            </label>
            <input
              type="text"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              className="w-full px-3 py-2 bg-[#14161f] border border-gray-700 rounded text-white focus:outline-none focus:border-blue-500"
              placeholder={`Enter new ${fieldLabel.toLowerCase()}`}
              autoFocus
            />
          </div>

          <div className="flex gap-3 justify-end">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-300 bg-gray-700 hover:bg-gray-600 rounded transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded transition-colors"
            >
              Update
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}